/* eslint-disable no-case-declarations */
import { fromJS } from "immutable"
import moment from "moment"
import bulkEmailActions, { EmailPreviewStatusCodes } from "shared/outbox/action-types"
import searchifyActionTypes from "shared/search/searchify/action-types"

/**
 * getDefaultBulkEmailValues
 *
 * Most of these have been moved to model/DB constraints on the backend, but these M2M defaults and the scheduled_send_time
 * fields don't lend themselvces well to that pattern... for now!
 *
 * @returns {object} Object containing all of the keys needed for a single email
 */
export const getDefaultBulkEmailValues = () => ({
    // M2M Fields
    limited_to_teams: Userdata.default_limited_to_teams,

    // This field is blank on creation, but when we create the email we set it to now, so the scheduler is initialized with a valid time
    // ie: Not in the past. When loading existing scheduled emails, it will be in the past anyways...
    scheduled_send_time: moment().format(),
})

export const defaultState = fromJS({
    // Status flags
    posting: false,

    // Keep track of the active bulkEmail ID
    bulkEmailId: undefined,

    // Hold the active bulkEmail and any ABTest variants if applicable
    bulkEmails: {},
})

export const getInitialState = (additionalArgs) => defaultState.merge(fromJS(additionalArgs))

/**
 * Reducer for all of the database actions around bulk emails
 *
 * @name bulkEmailProfileReducer
 *
 * @param {object} state - the state
 * @param {object} action - Object with type and payload
 * @returns {object} - the state for bulk emails
 */
const bulkEmailProfileReducer = (state = defaultState, action) => {
    switch (action.type) {
        case bulkEmailActions.SET_ACTIVE_BULK_EMAIL_ID:
            return state.set("bulkEmailId", String(action.bulkEmailId))
        case bulkEmailActions.SET_IS_FROM_CAMPAIGN:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "isFromCampaign"], true)
        /* Posting Bulk Emails */
        case bulkEmailActions.POST_BULK_EMAIL_START:
            return state.set("posting", true)
        case bulkEmailActions.POST_BULK_EMAIL_FAIL:
            return state.set("posting", false)
        case bulkEmailActions.POST_BULK_EMAIL_SUCCESS:
            return state
                .setIn(
                    ["bulkEmails", String(action.response.id)],
                    fromJS(getDefaultBulkEmailValues()).merge(fromJS(action.response)),
                )
                .set("bulkEmailId", String(action.response.id))
                .set("posting", false)

        case bulkEmailActions.GET_BULK_EMAIL_START:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "fetching"], true)
        case bulkEmailActions.GET_BULK_EMAIL_FAIL:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "fetching"], false)
        case bulkEmailActions.GET_BULK_EMAIL_SUCCESS:
            return state
                .setIn(
                    ["bulkEmails", String(action.bulkEmailId)],
                    fromJS({
                        ...action.response,
                        fetching: false,
                    }),
                )
                .set("bulkEmailId", String(action.bulkEmailId))

        case bulkEmailActions.GET_AB_VARIANTS_START:
        case bulkEmailActions.GET_AB_VARIANTS_FAIL:
            return state
        case bulkEmailActions.GET_AB_VARIANTS_SUCCESS:
            return state.mergeIn(
                ["bulkEmails"],
                fromJS({
                    ...action.response.objects
                        // Copy the attachments from _extra to attachments
                        .map((variant) => ({
                            // eslint-disable-next-line no-underscore-dangle
                            attachments: variant._extra.attachments || undefined,
                            ...variant,
                        }))
                        // Save them under their own bulk_email_id keys
                        .reduce((acc, curr) => {
                            acc[curr.id] = curr
                            return acc
                        }, {}),
                }),
            )

        /* Basic Store Changes */
        case bulkEmailActions.UPDATE_BULK_EMAIL:
            // For updating the root bulk email
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    ...action.payload,
                    lastUpdated: moment(),
                }),
            )

        case bulkEmailActions.UPDATE_ABTEST:
            // For updating the nested ABTest object in a BulkEmail

            // If there is no "abtest" key, this is not an ABTest email
            // This action should not have been used; we should not add an abtest, just return
            if (state.getIn(["bulkEmails", String(action.bulkEmailId), "abtest"]) === null) {
                return state
            }

            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId), "abtest"],
                fromJS({
                    ...action.payload,
                }),
            )

        /**
         * Patching
         *  - these actions happen when we are pushing an update to the
         *    database
         */
        case bulkEmailActions.PATCH_BULK_EMAIL_START:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "patching"], true)
        case bulkEmailActions.PATCH_BULK_EMAIL_FAIL:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "patching"], false)
        case bulkEmailActions.PATCH_BULK_EMAIL_SUCCESS:
            // if we are updating the selection - update the counts
            return state
                .mergeIn(
                    ["bulkEmails", String(action.bulkEmailId)],
                    fromJS({
                        patching: false,
                        is_custom_unsubscribe: action.response.is_custom_unsubscribe,
                        is_draft: action.response.is_draft,
                        person_count: action.response.person_count,
                        press_count: action.response.press_count,
                        supporter_count: action.response.supporter_count,
                        email_count: action.response.email_count,
                        excluded_email_count: action.response.excluded_email_count,
                        unsubscribe_enabled: action.response.unsubscribe_enabled,
                        content_version: action.response.content_version,
                    }),
                )
                .mergeDeepIn(["selection_json"], action.response.selection_json)

        case bulkEmailActions.PATCH_BULK_EMAIL_M2M_START:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "patching"], true)
        case bulkEmailActions.PATCH_BULK_EMAIL_M2M_FAIL:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "patching"], false)
        case bulkEmailActions.PATCH_BULK_EMAIL_M2M_SUCCESS:
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    [action.field]: action[action.field],
                    patching: false,
                }),
            )

        // autosaving
        case bulkEmailActions.AUTOSAVE_BULK_EMAIL_START:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "autosaving"], true)
        case bulkEmailActions.AUTOSAVE_BULK_EMAIL_BLOCKED:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "autosaving"], false)
        case bulkEmailActions.AUTOSAVE_BULK_EMAIL_SUCCESS:
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    autosaving: false,
                    lastAutosaved: moment(),
                }),
            )

        // for updating recipients
        case bulkEmailActions.UPDATE_RECIPIENTS_START:
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    ...action.recipientsObject,
                    isCounting: true,
                }),
            )
        case bulkEmailActions.UPDATE_RECIPIENTS_FAIL:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "isCounting"], false)
        case bulkEmailActions.UPDATE_RECIPIENTS_SUCCESS:
            // if we have reset the recipients then we want to update the placeholder status
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "isCounting"], false)

        /* PLACEHOLDER THINGS */

        // for updating the placeholders
        case bulkEmailActions.UPDATE_PLACEHOLDERS_BULK_EMAIL_START:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "updatingPlaceholders"], true)
        case bulkEmailActions.UPDATE_PLACEHOLDERS_BULK_EMAIL_FAIL:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "updatingPlaceholders"], false)
        case bulkEmailActions.UPDATE_PLACEHOLDERS_BULK_EMAIL_SUCCESS:
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    placeholders: action.response,
                    updatingPlaceholders: false,
                }),
            )

        // for validation of placeholders
        case bulkEmailActions.VALIDATE_PLACEHOLDERS_BULK_EMAIL_START:
            // update the status of the placeholders
            return state.setIn(
                ["bulkEmails", String(action.bulkEmailId), "emailPlaceholderStatus"],
                EmailPreviewStatusCodes.LOADING,
            )
        case bulkEmailActions.VALIDATE_PLACEHOLDERS_BULK_EMAIL_FAIL:
            // if it fails then we can't do much... set back to ready
            return state.setIn(
                ["bulkEmails", String(action.bulkEmailId), "emailPlaceholderStatus"],
                EmailPreviewStatusCodes.INVALID,
            )
        case bulkEmailActions.VALIDATE_PLACEHOLDERS_BULK_EMAIL_SUCCESS:
            // on success we want to update the placeholders
            if (action.response.valid) {
                // if the response is valid then we can clear the invalid status
                return state.mergeIn(
                    ["bulkEmails", String(action.bulkEmailId)],
                    fromJS({
                        invalidPlaceholders: [],
                        emailPlaceholderStatus: EmailPreviewStatusCodes.VALID,
                    }),
                )
            }

            // if the response was bad, then we want to reset
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    invalidPlaceholders: action.response.errors,
                    emailPlaceholderStatus: EmailPreviewStatusCodes.INVALID,
                }),
            )

        // on preview get
        case bulkEmailActions.GET_PREVIEW_BULK_EMAIL_START:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "gettingPreviews"], true)
        case bulkEmailActions.GET_PREVIEW_BULK_EMAIL_FAIL:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "gettingPreviews"], false)
        case bulkEmailActions.GET_PREVIEW_BULK_EMAIL_SUCCESS:
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    previews:
                        state.getIn(["bulkEmails", String(action.bulkEmailId), "previewIndex"]) > 0
                            ? state
                                  .getIn(["bulkEmails", String(action.bulkEmailId), "previews"])
                                  .concat(fromJS(action.response.previews))
                            : action.response.previews,
                    gettingPreviews: false,
                }),
            )

        // for resetting
        case bulkEmailActions.RESET_BULK_EMAIL_PREVIEWS:
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    gettingPreviews: false,
                    previewIndex: 0,
                    previews: [],
                    invalidPlaceholders: [],
                    emailPlaceholderStatus: EmailPreviewStatusCodes.READY,
                }),
            )

        /* Canceling */
        case bulkEmailActions.CANCEL_BULK_EMAIL_START:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "canceling"], true)
        case bulkEmailActions.CANCEL_BULK_EMAIL_FAIL:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "canceling"], false)
        case bulkEmailActions.CANCEL_BULK_EMAIL_SUCCESS:
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    canceling: false,
                    current_status: DjangIO.app.userdata.outbox.types.EmailStatus.draft.value,
                    is_draft: true,
                }),
            )

        /* Duplicating Attachments */
        case bulkEmailActions.DUPLICATE_ATTACHMENTS_START:
        case bulkEmailActions.DUPLICATE_ATTACHMENTS_FAIL:
            return state
        case bulkEmailActions.DUPLICATE_ATTACHMENTS_SUCCESS:
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    attachments: action.response.attachments,
                }),
            )

        /* TEST Emails */
        case bulkEmailActions.TESTING_BULK_EMAIL_START:
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    current_status: window.DjangIO.app.userdata.outbox.types.EmailStatus.draft.value,
                    is_draft: true,
                    testing: true,
                }),
            )
        case bulkEmailActions.TESTING_BULK_EMAIL_FAIL:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "testing"], false)
        case bulkEmailActions.TESTING_BULK_EMAIL_SUCCESS:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "testing"], false)

        /* SENDING */
        case bulkEmailActions.SENDING_BULK_EMAIL_START:
            // when you start the send, make sure to update the state
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    allowedUnload: true,
                    current_status: window.DjangIO.app.userdata.outbox.types.EmailStatus.injecting.value,
                    is_draft: false,
                    scheduled_send_time: state.getIn(["bulkEmails", String(action.bulkEmailId), "is_scheduled"])
                        ? state.getIn(["bulkEmails", String(action.bulkEmailId), "scheduled_send_time"])
                        : moment().format(),
                    sending: true,
                }),
            )
        case bulkEmailActions.SENDING_BULK_EMAIL_FAIL:
            return state.mergeIn(
                ["bulkEmails", String(action.bulkEmailId)],
                fromJS({
                    allowedUnload: false,
                    current_status: window.DjangIO.app.userdata.outbox.types.EmailStatus.draft.value,
                    is_draft: true,
                    sending: false,
                }),
            )
        case bulkEmailActions.SENDING_BULK_EMAIL_SUCCESS:
            // The slice will be reset after the action creator segues back to the overview page
            return state

        /* EXTERNAL THINGS */
        case bulkEmailActions.START_ADV_SELECTION:
            // if we are starting an 'Advanced selection' hold the initial object here
            return state.set("initialOutboxSearch", fromJS(action.initialOutboxSearch)).set("usingAdvancedSearch", true)

        case searchifyActionTypes.SEARCHIFY_SUBMIT:
        case searchifyActionTypes.SEARCHIFY_CLEAR:
            // do whatever u want...shouldn't need anything because
            // we'll replace the search frame with outbox
            return state.set("usingAdvancedSearch", false).set("initialOutboxSearch", null)

        // TODO(Peer Review): Lets get rid of this altogether (UX decision), or at least make it an updateBulkEmail
        case bulkEmailActions.OUTBOX_SET_SUBJECT:
            return state.setIn(["bulkEmails", String(action.bulkEmailId), "subject"], action.value)

        // TODO(Peer Review):This should be keyeed to the spcific BulkEmail ID
        case bulkEmailActions.RESET_BULK_EMAIL:
            // If we have a bulkEmailId, clear it from the store (ex: archiving an email)
            if (action.bulkEmailId) {
                return state.deleteIn(["bulkEmails", String(action.bulkEmailId)]).set("bulkEmailId", undefined)
            }

            // Otherwise, leave the bulk email in the store, but clear our currently selected ID
            return state.set("bulkEmailId", undefined)

        default:
            return state
    }
}

export default bulkEmailProfileReducer
